import React from 'react';
import { FormattedMessage } from 'react-intl';
import { SearchOrigin } from '@alltrails/analytics/amplitudeEnums';
import { logExploreFilterSheetViewed } from '@alltrails/analytics/amplitudeFunctions';
import AlgoliaSearchBox from 'components/AlgoliaSearchBox';
import FilterBar from 'components/explore/FilterBar';
import MobileExploreMapSwitch from 'components/MobileExploreMapSwitch';
import useSearchFilters from 'hooks/useSearchFilters';
import iconFilterSrc from 'images/icons/search/icon-filter.svg';
import iconFilterActiveSrc from 'images/icons/search/icon-filter-active.svg';
import { Context } from 'types/Context';
import { FilterConfig, FilterRangeUpdates, Filters } from 'types/Search';
import { AlgoliaResults } from 'types/Search/Algolia';
import { EXPLORE } from 'utils/constants/SearchAnalyticsConstants';
import legacyPerformSearch from 'utils/legacyPerformSearchUtil';
import NestedPanel from '../filters/NestedPanel';
import { getSearchBarFiltersConfigurations } from './searchFiltersConfiguration';
import * as styles from './styles/styles.module.scss';

export type Props = {
  algoliaIndex: any;
  allowStringSearch: boolean;
  atMaps: any;
  clearRadioFilter?: (filterKey: string, pageName: string) => void;
  context: Context;
  defaultLatLng: any;
  enabledFilters: string[];
  filters: Filters;
  handleFilterRadioToggle?: (filterKey: string, filterConfigKey: string) => void;
  handleFilterSet?: (filterKey: string, value: string) => void;
  handleFilterSlider?: (filterKey: string, value: FilterRangeUpdates) => void;
  handleFilterToggle?: (filterKey: string, filterConfigKey: string) => void;
  handleObjectSelected: () => void;
  handleQueryTermSet: (queryString: string) => void;
  handleTrailSelected: () => void;
  initialUserSlug: string;
  isMobileWidth: boolean;
  listMethods: any;
  liveStringSearch: boolean;
  page: string;
  placeholderText: string;
  resultsCount: number;
  typeSubsets: any;
};

const SearchFilterBar = ({
  algoliaIndex,
  allowStringSearch,
  atMaps,
  clearRadioFilter,
  context,
  defaultLatLng,
  enabledFilters,
  filters,
  handleFilterRadioToggle,
  handleFilterSet,
  handleFilterSlider,
  handleFilterToggle,
  handleObjectSelected,
  handleQueryTermSet,
  handleTrailSelected,
  initialUserSlug,
  isMobileWidth,
  listMethods,
  liveStringSearch,
  page,
  placeholderText,
  resultsCount,
  typeSubsets
}: Props) => {
  const trackMobileFiltersClick = () => {
    const searchOrigin = filters.mobileMap ? SearchOrigin.ExploreMapView : SearchOrigin.ExploreListView;

    logExploreFilterSheetViewed({ search_origin: searchOrigin });
  };

  const sortedFilters = getSearchBarFiltersConfigurations(filters);

  // We have some overlapping work with the FilterBar here. The FilterBar expects
  // the entire sortedFilters array and has logic to reduce the filters and
  // display them where they belong. But the mobile web filters UI expects every
  // filter regardless of "nested" or "admin" status, so we have to filter out
  // inaccessible data here.
  // https://alltrails.atlassian.net/browse/DISCO-159
  const allFiltersForMobile = sortedFilters.filter(nextFilter => {
    if (!enabledFilters.includes(nextFilter.enabledKey)) {
      return false;
    }

    if (nextFilter.admin && (!context?.adminUser || !Object.keys(nextFilter.config).length)) {
      return false;
    }

    return true;
  });

  const { activeFilters } = useSearchFilters({ filters: sortedFilters });

  const searchResultsCountFunction = (changes: { [key: string]: FilterConfig }): Promise<number> =>
    new Promise(resolve => {
      legacyPerformSearch({
        algoliaIndex,
        atMaps,
        context,
        filters: {
          ...filters,
          ...changes
        },
        // For /explore pages and typical trail searches this handler is called.
        handleSearchResults: (results: AlgoliaResults) => resolve(results?.hits?.length ?? 0),
        // If viewing the current user's custom maps this handler is called.
        // Note this isn't actually a search. This is a filter on atMaps.
        handleUserResults: (results: unknown[]) => resolve(results?.length ?? 0),
        initialUserSlug,
        isMobileWidth,
        listMethods,
        page
      });
    });

  return (
    <div className={styles.container}>
      <div className={styles.searchBoxAndButtons}>
        <AlgoliaSearchBox
          algoliaIndexId={context.primaryAlgoliaIndexId}
          className={styles.search}
          handleTrailSelect={handleTrailSelected}
          handleAreaSelect={handleObjectSelected}
          handleLocationSelect={handleObjectSelected}
          handleQueryStringSubmit={(queryString: string) => handleQueryTermSet(queryString.trim())}
          allowStringSearch={allowStringSearch}
          liveStringSearch={liveStringSearch}
          onSearchPage
          numResults={isMobileWidth ? 8 : 10}
          placeholderText={placeholderText}
          currentUser={context.currentUser}
          mobileBrowser={context.mobileBrowser}
          languageRegionCode={context.languageRegionCode}
          typeSubset={typeSubsets}
          defaultLatLng={defaultLatLng}
          listTrailIds={filters.trailIds}
          useNewSearchExperience
          page={EXPLORE}
        />

        <div className={styles.buttons}>
          <NestedPanel
            clearRadioFilter={clearRadioFilter}
            handleFilterRadioToggle={handleFilterRadioToggle}
            handleFilterSet={handleFilterSet}
            handleFilterSlider={handleFilterSlider}
            handleFilterToggle={handleFilterToggle}
            page={page}
            resultsCount={resultsCount}
            searchResultsCountFunction={searchResultsCountFunction}
            sortedFilters={allFiltersForMobile}
            title={<FormattedMessage defaultMessage="Filters" />}
            triggerElement={
              <span>
                <button type="button" className={styles.filtersModalButton} onClick={trackMobileFiltersClick}>
                  <img alt="full screen filters modal" src={activeFilters?.length ? iconFilterActiveSrc : iconFilterSrc} />
                </button>
              </span>
            }
          />
          <MobileExploreMapSwitch showMapMobile={filters.mobileMap} />
        </div>
      </div>

      <FilterBar
        clearRadioFilter={clearRadioFilter}
        context={context}
        enabledFilters={enabledFilters}
        handleFilterRadioToggle={handleFilterRadioToggle}
        handleFilterSet={handleFilterSet}
        handleFilterSlider={handleFilterSlider}
        handleFilterToggle={handleFilterToggle}
        sortedFilters={sortedFilters}
        page={page}
        resultsCount={resultsCount}
        searchResultsCountFunction={searchResultsCountFunction}
      />
    </div>
  );
};

export default SearchFilterBar;
